import type { SupportCategory, SupportPriority, SupportStatus, SupportTicket } from "./types";

/**
 * The words the support surfaces use, and the rules about what may be said.
 *
 * Kept out of the components because two of them are load-bearing rather than
 * decorative: the status wording is the customer's answer to "is anybody
 * waiting on me", and nothing anywhere in here may state or imply a response
 * time. The platform does not sell one, and a label reading "urgent — answered
 * first" would be a commitment made by a constant.
 */

/**
 * The four statuses, in the order the tabs offer them.
 *
 * Their words live in `support.json` under `status.<value>`, and the reason
 * they are copy rather than the column's value is worth keeping here: "open"
 * and "pending" are the platform's vocabulary and describe the queue, not the
 * person looking at the screen — a customer seeing "pending" on a thread we
 * already answered reasonably concludes we have not got to it. Whatever a
 * language calls them, that distinction is the thing being translated.
 */
export const STATUSES: SupportStatus[] = ["open", "pending", "resolved", "closed"];

/**
 * Badge tones, off the semantic scale.
 *
 * `warn` for "waiting on you" is the one worth explaining: amber means
 * something needs attention, and here the attention needed is the customer's.
 * `mint` is the platform's reserved good-and-done colour, which resolved is.
 */
export const STATUS_TONE: Record<SupportStatus, { styles: string; dot: string }> = {
  open: { styles: "border-accent/25 bg-accent/8 text-accent", dot: "bg-accent" },
  pending: { styles: "border-warn/25 bg-warn/8 text-warn", dot: "bg-warn" },
  resolved: { styles: "border-mint/25 bg-mint/8 text-mint", dot: "bg-mint" },
  closed: { styles: "border-line bg-raised text-fg-muted", dot: "bg-fg-subtle" },
};

/** The categories, in the order they are offered. Words in `support.json`. */
export const CATEGORIES: SupportCategory[] = [
  "technical",
  "billing",
  "account",
  "abuse",
  "other",
];

/** The impact levels, low to high. Words in `support.json`. */
export const PRIORITIES: SupportPriority[] = ["low", "normal", "high", "urgent"];

/*
 * A note that travels with the words rather than with this list, and has to
 * survive translation: no wording under `priority.*.hint` may promise anything
 * about when an answer arrives. The value is real — it tells us what is at
 * stake, which changes what we look at first — and describing it as a queue
 * position would be selling something that does not exist.
 */

/** Whose turn it is, which the status encodes and the list renders as a marker. */
export function waitingOn(ticket: SupportTicket): "us" | "you" | null {
  if (ticket.status === "open") return "us";
  if (ticket.status === "pending") return "you";
  return null;
}

/** A ticket nobody can write on any more. */
export function isLocked(ticket: { status: SupportStatus }): boolean {
  return ticket.status === "closed";
}

/*
 * The upload rules, mirrored from the API's `services/support.ts`.
 *
 * Duplicated deliberately rather than fetched: the point of having them here is
 * to refuse a 12 MB video *before* it is uploaded over somebody's tethered
 * connection, which cannot be done by asking the server. The API is still the
 * authority — everything here is a courtesy, and a mismatch costs a rejected
 * request rather than a wrong answer.
 */
export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENTS_PER_MESSAGE = 5;

export const ATTACHMENT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "text/plain",
  "text/csv",
  "application/json",
  "application/pdf",
  "application/zip",
  "application/gzip",
] as const;

/** What the file picker offers. Extensions as well as types: macOS filters on both. */
export const ATTACHMENT_ACCEPT = [...ATTACHMENT_TYPES, ".log", ".txt", ".json", ".csv"].join(",");

/** The types a browser may render inline. Never SVG — see the API's allowlist. */
export function isPreviewable(contentType: string): boolean {
  return ["image/png", "image/jpeg", "image/gif", "image/webp"].includes(contentType);
}

/** Which refusal applies to a file, and what the sentence needs to name it. */
export interface AttachmentProblem {
  key: "tooMany" | "empty" | "tooLarge" | "wrongType";
  values: { count?: number; name?: string };
}

/**
 * Why a file cannot be attached, or null.
 *
 * Names the refusal rather than writing it out: this is a plain module shared
 * by two screens and cannot reach a translator, and the caller is a component
 * that can. What stays here is the decision, which is the part with rules in
 * it.
 *
 * A `.log` from a build often arrives with an empty type or
 * `application/octet-stream` depending on the operating system, so a plain-text
 * extension is accepted when the browser has no opinion — the alternative is
 * refusing the single most useful thing anybody attaches.
 */
export function attachmentProblem(file: File, alreadyPicked: number): AttachmentProblem | null {
  if (alreadyPicked >= MAX_ATTACHMENTS_PER_MESSAGE) {
    return { key: "tooMany", values: { count: MAX_ATTACHMENTS_PER_MESSAGE } };
  }
  if (file.size === 0) return { key: "empty", values: { name: file.name } };
  if (file.size > ATTACHMENT_MAX_BYTES) {
    return { key: "tooLarge", values: { name: file.name } };
  }
  if (!(ATTACHMENT_TYPES as readonly string[]).includes(effectiveType(file))) {
    return { key: "wrongType", values: { name: file.name } };
  }
  return null;
}

/**
 * The type to send for a file whose own is missing or generic.
 *
 * Only ever widens to `text/plain`, and only on an extension that is text by
 * definition. Guessing anything richer from a filename is how an executable
 * gets uploaded as a PDF.
 */
export function effectiveType(file: File): string {
  if (file.type && file.type !== "application/octet-stream") return file.type;
  return /\.(log|txt)$/i.test(file.name) ? "text/plain" : file.type;
}

/**
 * A file with the corrected type, when the browser gave none.
 *
 * `File` is immutable, so this rebuilds it — cheap, because the blob's bytes are
 * shared rather than copied.
 */
export function withEffectiveType(file: File): File {
  const type = effectiveType(file);
  return type === file.type ? file : new File([file], file.name, { type });
}
